import React, { useCallback, useMemo } from 'react';
import { Options } from 'react-select';
import { flatten, isNull, set } from 'lodash/fp';
import { isEqual } from 'lodash';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { DeleteButton } from '../../../shared/button/DeleteButton';
import { PlusButton } from '../../../shared/button/PlusButton';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Text } from '../../../shared/text/Text';
import { getAllDropdownLists } from '../../dropdown-lists/store';
import { addCompanyBicCode, AIGeneratedEntityField, CompanyEntity, CompanyEntityContent, EntityAIFieldKey, OpinionSubCounterpartyType, removeCompanyBicCode, updateEntityAIField, updateEntityContent } from '../store';
import { AiDropdownField } from './AiDropdownField';
import styles from './EntityType.module.scss';
import { MiFIDOptions, emirOptions, opinionCounterpartyTypeOptions } from '../../../constants/entity';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { getAvailableSubCounterpartyTypes } from '../../sub-counterparty-types/store';
import { OpinionScope } from '../../opinions/store';
import { DropdownSummary } from '../../../shared/dropdown/DropdownSummary';
import { scopeFormatter } from '../../../shared/table/arkTableFormatters';
import { InformationTooltip } from '../../../shared/tooltip';

interface CompanyEntityDetailsSectionProps {
    entity: CompanyEntity;
}

export const CompanyEntityDetailsSection: React.FC<CompanyEntityDetailsSectionProps> = ({ entity }) => {

    const dispatch = useAppDispatch();
    const handleUpdateContent = useCallback((key: keyof CompanyEntityContent, value: string | string[] | Date | number | null | OpinionSubCounterpartyType[]) => dispatch(updateEntityContent(key, value)), [dispatch]);
    const updateAIFieldContent = useCallback((key: EntityAIFieldKey, secondaryKey: keyof AIGeneratedEntityField, value: string | null | boolean) => dispatch(updateEntityAIField(key, secondaryKey, value)), [dispatch]);
    const addBicCode = () => dispatch(addCompanyBicCode());
    const deleteBicCode = (index: number) => dispatch(removeCompanyBicCode(index));
    const allDropdowns = useAppSelector(getAllDropdownLists);
    const availableSubCounterpartyTypes = useAppSelector(getAvailableSubCounterpartyTypes);
    const { content: { MiFIDClassification, emirClassification, companyNumber, counterpartyType, primaryContact, opinionCounterpartyType, opinionSubCounterpartyTypes } } = entity;

    const updateBicCode = (index: number, value: string) => handleUpdateContent('bic', entityBIC.map((bic, i) => i === index ? value : bic));

    const counterpartyTypeDropdownOptions = allDropdowns.find(({ name }) => name === 'CounterpartyType')?.options || [];

    const updateCounterpartyType = (value: string | null) => updateAIFieldContent('counterpartyType', 'value', value);
    const updateOpinionCounterpartyType = (value: string | null) => {
        updateAIFieldContent('opinionCounterpartyType', 'value', value);
        if (isNull(value) || value !== opinionCounterpartyType?.value) {
            handleUpdateContent('opinionSubCounterpartyTypes', []);
        }
    };
    const updateEmirClassification = (value: string | null) => updateAIFieldContent('emirClassification', 'value', value);
    const updateMiFIDClassification = (value: string | null) => updateAIFieldContent('MiFIDClassification', 'value', value);

    const entityBIC = useMemo(() => entity.content.bic || [''], [entity.content.bic]);
    const showDeleteBIC = useMemo(() => entityBIC.length > 1, [entityBIC]);
    const entityBICWidth = useMemo(() => showDeleteBIC ? 'calc(100% - 30px)' : '100%', [showDeleteBIC]);

    const newSubCounterpartyType = useMemo(() => ({ scope: [], value: null }), []);
    const entityOpinionSubCounterpartyTypes = useMemo(() => opinionSubCounterpartyTypes.length > 0 ? opinionSubCounterpartyTypes : [newSubCounterpartyType], [newSubCounterpartyType, opinionSubCounterpartyTypes]);

    const showOpinionSubCounterpartyDelete = useMemo(() => entityOpinionSubCounterpartyTypes.length > 1, [entityOpinionSubCounterpartyTypes]);
    const opinionSubCounterpartyWidth = useMemo(() => showOpinionSubCounterpartyDelete ? 'calc(100% - 30px)' : '100%', [showOpinionSubCounterpartyDelete]);
    const hideSubCounterpartyType = useMemo(() => !entity.content.opinionJurisdiction || !opinionCounterpartyType?.value, [entity, opinionCounterpartyType]);

    const currentlyUsedScopes = useMemo(() => flatten(entityOpinionSubCounterpartyTypes.map(({ scope }) => scope)), [entityOpinionSubCounterpartyTypes]);
    const allScopesSelected = useMemo(() => Object.values(OpinionScope).every(scope => currentlyUsedScopes.includes(scope)), [currentlyUsedScopes]);

    const scopeOptions = useMemo(() => Object.values(OpinionScope).map(scope => ({ value: scope, label: scopeFormatter(scope) })), []);
    const getAvailableScopeOptions = useCallback((currentScopes: OpinionScope[]) => Object.values(OpinionScope).filter(scope => !(currentlyUsedScopes.includes(scope) && !currentScopes.includes(scope))).map(scope => ({ value: scope, label: scopeFormatter(scope) })), [currentlyUsedScopes]);

    const getAvailableSubCounterpartyTypeOptions = useCallback((selectedScopes: OpinionScope[]) => availableSubCounterpartyTypes.filter(({ scopes }) => selectedScopes.every(selectedScope => scopes.includes(selectedScope))).map(({ subCounterpartyName, subCounterpartyTypeId }) => ({ value: subCounterpartyTypeId!.toString(), label: subCounterpartyName })), [availableSubCounterpartyTypes]);

    const getScopeValueForSubCounterpartyType = useCallback((scopes: OpinionScope[]) => scopeOptions.filter(({ value }) => scopes.includes(value)), [scopeOptions]);
    const getSubCounterpartyValue = useCallback((opinionSubCounterpartyTypeId: number | null, scopes: OpinionScope[]) => {
        let option: DropdownOption | null = null;
        if (!isNull(opinionSubCounterpartyTypeId)) {
            const optionsForScopes = getAvailableSubCounterpartyTypeOptions(scopes);
            const label = optionsForScopes.find(({ value }) => value === opinionSubCounterpartyTypeId.toString())?.label || '';
            return { value: opinionSubCounterpartyTypeId.toString(), label };
        }
        return option;
    }, [getAvailableSubCounterpartyTypeOptions]);

    const handleUpdateSubCounterparty = (key: keyof OpinionSubCounterpartyType, option: DropdownOption | Options<DropdownOption> | null, index: number) => {
        let updatedOpinionSubCounterpartyTypes = entityOpinionSubCounterpartyTypes;
        if (key === 'scope') {
            const selectedScopes = (option as Options<DropdownOption>).map(({ value }) => value) as OpinionScope[];
            const availableOptionsForScopes = getAvailableSubCounterpartyTypeOptions(selectedScopes).map(({ value }) => parseInt(value));
            const currentSelectedSubCounterpartyId = entityOpinionSubCounterpartyTypes[index].value;
            updatedOpinionSubCounterpartyTypes = set(`[${index}].scope`, selectedScopes, updatedOpinionSubCounterpartyTypes);
            if (!isNull(currentSelectedSubCounterpartyId) && !availableOptionsForScopes.includes(currentSelectedSubCounterpartyId)) {
                updatedOpinionSubCounterpartyTypes = set(`[${index}].value`, null, updatedOpinionSubCounterpartyTypes);
            }
        } else {
            let selected = null;
            if (!isNull(option)) {
                selected = parseInt((option as DropdownOption).value);
            }
            updatedOpinionSubCounterpartyTypes = set(`[${index}].value`, selected, updatedOpinionSubCounterpartyTypes);
        }
        if (isEqual(updatedOpinionSubCounterpartyTypes, [newSubCounterpartyType])) {
            updatedOpinionSubCounterpartyTypes = [];
        }
        handleUpdateContent('opinionSubCounterpartyTypes', updatedOpinionSubCounterpartyTypes);
    };

    const addSubCounterpartyRow = useCallback(() => {
        const updatedOpinionSubCounterpartyTypes = [...entityOpinionSubCounterpartyTypes, newSubCounterpartyType];
        handleUpdateContent('opinionSubCounterpartyTypes', updatedOpinionSubCounterpartyTypes);
    }, [handleUpdateContent, entityOpinionSubCounterpartyTypes, newSubCounterpartyType]);

    const removeSubCounterpartyRow = useCallback((removeIndex: number) => {
        let updatedOpinionSubCounterpartyTypes = entityOpinionSubCounterpartyTypes.filter((_, index) => index !== removeIndex);
        if (isEqual(updatedOpinionSubCounterpartyTypes, [newSubCounterpartyType])) {
            updatedOpinionSubCounterpartyTypes = [];
        }
        handleUpdateContent('opinionSubCounterpartyTypes', updatedOpinionSubCounterpartyTypes);
    }, [handleUpdateContent, entityOpinionSubCounterpartyTypes, newSubCounterpartyType]);

    return (
        <div className={styles.detailsWrapper}>
            <Scrollable>
                <div className={styles.scrollableWrapper}>
                    <Text
                        label='Company Number'
                        testId='entity-company-number'
                        onChange={e => handleUpdateContent('companyNumber', e.target.value)}
                        value={companyNumber || ''}
                        placeholder='Company Number...'
                    />
                    <Text
                        label='Primary Contact'
                        testId='entity-primary-contact'
                        onChange={e => handleUpdateContent('primaryContact', e.target.value)}
                        value={primaryContact}
                        placeholder='Primary Contact...'
                    />
                    <AiDropdownField
                        label='MiFID Classification'
                        testId='entity-mifid-classification'
                        updateValue={updateMiFIDClassification}
                        toggleConfirmation={value => updateAIFieldContent('MiFIDClassification', 'aiConfirmed', value)}
                        field={MiFIDClassification}
                        options={MiFIDOptions}
                    />
                    <AiDropdownField
                        label='EMIR Classification'
                        testId='entity-emir-classification'
                        updateValue={updateEmirClassification}
                        toggleConfirmation={value => updateAIFieldContent('emirClassification', 'aiConfirmed', value)}
                        field={emirClassification}
                        options={emirOptions}
                    />
                    <AiDropdownField
                        label='Counterparty Type'
                        testId='entity-counterparty-type'
                        updateValue={updateCounterpartyType}
                        toggleConfirmation={value => updateAIFieldContent('counterpartyType', 'aiConfirmed', value)}
                        field={counterpartyType}
                        options={counterpartyTypeDropdownOptions}
                    />
                    <AiDropdownField
                        label='Opinion Counterparty Type'
                        testId='entity-opinion-counterparty-type'
                        updateValue={updateOpinionCounterpartyType}
                        field={opinionCounterpartyType}
                        options={opinionCounterpartyTypeOptions}
                        toggleConfirmation={value => updateAIFieldContent('opinionCounterpartyType', 'aiConfirmed', value)}
                    />
                    {!hideSubCounterpartyType && <div className={styles.inputWrapper}>
                        <div className={styles.inputWithPlusButtonWrapper}>
                            <div className={styles.label}>Opinion Sub Counterparty Type</div>
                            <PlusButton onClick={addSubCounterpartyRow} fontSize={15} disabled={allScopesSelected} />
                            <div className={styles.subCounterpartyTooltip}>
                                <InformationTooltip content='This list is driven by the sub counterparty types available for the given parent and opinion jurisdiction. Choose the opinion scope(s) in the left hand dropdown and then select from the available sub counterparty types on the right hand side.' />
                            </div>
                        </div>
                        {entityOpinionSubCounterpartyTypes.map((subCounterpartyType, index) => {
                            const { scope, value } = subCounterpartyType;
                            const scopeValue = getScopeValueForSubCounterpartyType(scope);
                            const subCounterpartyValue = getSubCounterpartyValue(value, scope);
                            const subCounterpartyOptions = getAvailableSubCounterpartyTypeOptions(scope);
                            const availableScopeOptions = getAvailableScopeOptions(scope);
                            const subCounterpartyInputDisabled = scopeValue.length === 0;
                            return (
                                <div key={index} className={styles.subCounterpartyTypeWrapper}>
                                    <div className={styles.scopeAndTypeWrapper} style={{ width: opinionSubCounterpartyWidth }}>
                                        <div className={styles.dropdownWrapper}>
                                            <DropdownSummary
                                                onChange={e => handleUpdateSubCounterparty('scope', e, index)}
                                                value={scopeValue}
                                                options={availableScopeOptions}
                                                menuPortalTarget={document.body}
                                                isMulti
                                                showWhenAllSelected={false}
                                            />
                                        </div>
                                        <div className={styles.dropdownWrapper}>
                                            <Dropdown
                                                testId='entity-opinion-sub-counterparty-type'
                                                onChange={e => handleUpdateSubCounterparty('value', e, index)}
                                                value={subCounterpartyValue}
                                                options={subCounterpartyOptions}
                                                disabled={subCounterpartyInputDisabled}
                                            />
                                        </div>
                                    </div>
                                    {showOpinionSubCounterpartyDelete && <DeleteButton onClick={() => removeSubCounterpartyRow(index)} fontSize={20} />}
                                </div>
                            );
                        })}
                    </div>}
                    <div data-testid='entity-bic-label' className={styles.inputWithPlusButtonWrapper}>
                        <div className={styles.label}>BIC Code</div>
                        <PlusButton onClick={addBicCode} fontSize={15} />
                    </div>
                    <div className={styles.bicWrapper}>
                        <Scrollable maxHeight='215px'>
                            {entityBIC.map((bic, index) => (
                                <div key={index} className={styles.bicCodeWrapper}>
                                    <Text
                                        testId={`entity-bic-${index}`}
                                        onChange={e => updateBicCode(index, e.target.value)}
                                        value={bic}
                                        marginBottom='0px'
                                        marginRight={showDeleteBIC ? '5px' : '0px'}
                                        width={entityBICWidth}
                                        placeholder='BIC Code...'
                                    />
                                    {showDeleteBIC && <DeleteButton onClick={() => deleteBicCode(index)} fontSize={20} />}
                                </div>
                            ))}
                        </Scrollable>
                    </div>
                </div>
            </Scrollable>
        </div>
    );
};
